/**
 * Hook do konfiguracji Material React Table z odpowiednimi ustawieniami
 */
import React from 'react';

import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { XSquare, Filter, FilterCircle } from 'react-bootstrap-icons';
import { IconButton } from '@mui/material';
import { useAppTheme } from 'theme/useAppTheme';
import { IWiseTableQueryParams, IWiseTableData } from 'components/wise/types';
import { getEffectiveColumnWidth } from 'components/wise/WiseTable/constants';
import styles from 'theme/components/wise/WiseTable/WiseTable.module.scss';

interface IUseWiseTableMaterialReactTableParams {
  columns: MRT_ColumnDef<any>[];
  data: any[];
  rowKey: string;
  queryParams: IWiseTableQueryParams;
  tableData?: IWiseTableData;
  handleSortChange: (sortBy: string | null, sortDirection: 'asc' | 'desc' | null) => void;
  handleReorder?: (data: { entityId: number; beforeEntityId: number | null; afterEntityId: number | null }) => void;
  hasReorderAction: boolean;
  isLoading: boolean;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  columnOrder?: string[];
  withAdvancedFilters?: boolean;
  showColumnFilterIcons?: boolean;
  onOpenColumnFilterDialog?: (columnId: string, anchorEl: HTMLElement | null) => void;
  getColumnHasFilter?: (columnId: string) => boolean;
}

export const useWiseTableMaterialReactTable = ({
  columns,
  data,
  rowKey,
  queryParams,
  tableData,
  handleSortChange,
  handleReorder,
  hasReorderAction,
  isLoading,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder: columnOrderProp,
  withAdvancedFilters = false,
  showColumnFilterIcons = true,
  onOpenColumnFilterDialog,
  getColumnHasFilter
}: IUseWiseTableMaterialReactTableParams) => {
  const theme = useAppTheme();
  const { t } = useTranslation();

  const columnsWithFilterAction = React.useMemo(() => {
    if (!withAdvancedFilters || !showColumnFilterIcons || !onOpenColumnFilterDialog) return columns;
    return columns.map((col) => {
      const def = col as MRT_ColumnDef<any>;
      if (def.id === 'actions') return { ...def, enableColumnFilter: false };
      const columnId = String(def.id);
      return {
        ...def,
        enableColumnFilter: true,
        Filter: () => {
          const hasFilter = getColumnHasFilter?.(columnId) ?? false;
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenColumnFilterDialog(columnId, e.currentTarget as HTMLElement);
              }}
              aria-label="Filtr kolumny"
              sx={{ padding: 0.25 }}
            >
              {hasFilter ? <FilterCircle size={16} /> : <Filter size={16} />}
            </IconButton>
          );
        }
      };
    });
  }, [columns, withAdvancedFilters, showColumnFilterIcons, onOpenColumnFilterDialog, getColumnHasFilter]);

  const tableMinWidth = React.useMemo(() => {
    const cols: MRT_ColumnDef<any>[] =
      withAdvancedFilters && showColumnFilterIcons ? columnsWithFilterAction : columns;
    return cols.reduce(
      (sum, col) => sum + getEffectiveColumnWidth(col.size),
      0
    );
  }, [columns, columnsWithFilterAction, withAdvancedFilters, showColumnFilterIcons]);

  const table = useMaterialReactTable({
    columns: withAdvancedFilters && showColumnFilterIcons ? columnsWithFilterAction : columns,
    data,
    getRowId: (row) => String(row[rowKey]),
    manualPagination: true,
    pageCount: tableData?.total_pages || 0,
    state: {
      sorting: queryParams.sortBy
        ? [
            {
              id: queryParams.sortBy,
              desc: queryParams.sortDirection === 'desc'
            }
          ]
        : [],
      isLoading,
      showSkeletons: isLoading,
      showLoadingOverlay: false,
      ...(columnVisibility !== undefined && { columnVisibility }),
      ...(columnOrderProp && columnOrderProp.length > 0 && { columnOrder: columnOrderProp })
    },
    enableHiding: true,
    ...(onColumnVisibilityChange && {
      onColumnVisibilityChange: (updaterOrValue: unknown) => {
        const next =
          typeof updaterOrValue === 'function'
            ? (updaterOrValue as (prev: Record<string, boolean>) => Record<string, boolean>)(
                columnVisibility ?? {}
              )
            : (updaterOrValue as Record<string, boolean>);
        onColumnVisibilityChange(() => next);
      }
    }),
    // Konfiguracja sortowania
    enableSorting: true,
    manualSorting: true,
    onSortingChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater(
              queryParams.sortBy
                ? [{ id: queryParams.sortBy, desc: queryParams.sortDirection === 'desc' }]
                : []
            )
          : updater;

      const nextSort = next?.[0];

      if (nextSort) {
        handleSortChange(nextSort.id, nextSort.desc ? 'desc' : 'asc');
      } else {
        handleSortChange(null, null);
      }
    },
    // Wyłączenie niepotrzebnych funkcji
    autoResetPageIndex: false,
    enableTopToolbar: false,
    initialState: {
      ...(withAdvancedFilters && showColumnFilterIcons && { showColumnFilters: true }),
      showGlobalFilter: false,
      columnPinning: {
        left: [],
        right: [],
      },
    },
    enableColumnActions: false,
    enableColumnFilters: withAdvancedFilters && showColumnFilterIcons,
    enableColumnFilterModes: false,
    enableBottomToolbar: false,
    enableColumnResizing: false,
    enableExpandAll: false,
    enableExpanding: false,
    // Row ordering (reorder)
    enableRowOrdering: hasReorderAction,
    ...(hasReorderAction && {
      displayColumnDefOptions: {
        'mrt-row-drag': {
          header: t('Przesuń'),
          size: 60,
          muiTableHeadCellProps: {
            sx: {
              fontWeight: 'bold',
              borderBottom: `1px solid ${theme.palette.app.tableBorder}`
            }
          },
          muiTableBodyCellProps: {
            sx: {
              padding: '8px',
              textAlign: 'center',
              borderBottom: `1px solid ${theme.palette.app.tableBorder}`
            }
          }
        }
      }
    }),
    muiRowDragHandleProps: hasReorderAction
      ? ({ table }) => ({
          onDragEnd: () => {
            const { draggingRow, hoveredRow } = table.getState();
            if (draggingRow?.index === hoveredRow?.index || !handleReorder) {
              return;
            }

            const isDraggingUp = (draggingRow?.index || 0) > (hoveredRow?.index || 0);
            const entityId = draggingRow?.original?.[rowKey];
            const beforeEntityId =
              hoveredRow?.index === 0
                ? null
                : isDraggingUp
                ? data[(hoveredRow?.index || 0) - 1]?.[rowKey]
                : hoveredRow?.original?.[rowKey];
            const afterEntityId =
              hoveredRow?.index === data.length - 1
                ? null
                : isDraggingUp
                ? hoveredRow?.original?.[rowKey]
                : data[(hoveredRow?.index || 0) + 1]?.[rowKey];

            handleReorder({
              entityId,
              beforeEntityId,
              afterEntityId
            });
          }
        })
      : undefined,
    layoutMode: 'semantic',
    muiTableProps: {
      className: classnames('wise-table', styles.table),
      sx: { minWidth: tableMinWidth, tableLayout: 'fixed' }
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'unset'
      }
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '8px',
        background: theme.palette.background.paper,
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: 'unset'
      }
    },
    muiTableBodyCellProps: {
      sx: {
        padding: '12px 8px',
        fontSize: '12px',
        borderBottom: `1px solid ${theme.palette.app.tableBorder}`,
        minHeight: 44
      }
    },
    muiSkeletonProps: ({ column }) => {
      const align = (column.columnDef.muiTableBodyCellProps as { align?: 'left' | 'center' | 'right' })?.align;
      const marginSx =
        align === 'right'
          ? { marginLeft: 'auto' }
          : align === 'center'
            ? { marginLeft: 'auto', marginRight: 'auto' }
            : undefined;
      return {
        height: 20,
        ...(marginSx && { sx: marginSx })
      };
    },
    renderEmptyRowsFallback: () => {
      return (
        <div className={styles.emptyState}>
          <XSquare className={styles.emptyStateIcon} size={32} />
          <span>Brak danych</span>
        </div>
      );
    }
  });

  return table;
};
